import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from 'src/axios';
import type { CardTemplate } from 'src/components/greeting-card/templates/types';
import { templateApi } from 'src/services/api';
import type { CreateTemplateDto, UpdateTemplateDto } from 'src/services/generated-api';

export interface ResolvedTemplate {
  id: string;
  code: string;
  kind: string;
  categories: string[];
  name: Record<string, string>;
  slots: CardTemplate['slots'];
  thumbnailKey: string | null;
  // Presigned URL for thumbnailKey, signed server-side by /template/resolve.
  // Null when the template has no rendered thumbnail (fall back to a static file).
  thumbnailUrl: string | null;
  /** Recommended paper ids (never a hard limit; empty = no recommendation). */
  paperIds: string[];
  /** Paper preselected on apply / used as the thumbnail backdrop. */
  showcasePaperId: string | null;
}

export function useResolvedTemplates(kind: string | undefined, codes: string[]) {
  const codesParam = codes.length ? codes.join(',') : undefined;
  return useQuery({
    queryKey: ['templates-resolve', kind ?? 'none', codesParam ?? 'all'],
    enabled: Boolean(kind),
    queryFn: async () => {
      const res = await axiosInstance.get<ResolvedTemplate[]>('/template/resolve', {
        params: { kind, ...(codesParam ? { codes: codesParam } : {}) },
      });
      return res.data;
    },
    staleTime: 60_000,
  });
}

export const templateKeys = {
  all: ['templates'] as const,
  one: (id: string) => ['templates', id] as const,
  library: () => ['templates', 'library'] as const,
};

export function useTemplate(id: string | undefined) {
  return useQuery({
    queryKey: templateKeys.one(id ?? 'none'),
    enabled: Boolean(id) && id !== 'new',
    queryFn: async () => {
      const res = await templateApi.getOneBaseTemplateControllerTemplate(id as string);
      return res.data;
    },
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateTemplateDto) => {
      const res = await templateApi.createOneBaseTemplateControllerTemplate(dto);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: templateKeys.all }),
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateTemplateDto }) => {
      const res = await templateApi.updateOneBaseTemplateControllerTemplate(id, data);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: templateKeys.all }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await templateApi.deleteOneBaseTemplateControllerTemplate(id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: templateKeys.all }),
  });
}

export interface TemplateLibraryItem {
  id: string;
  code: string;
  kind: string;
  categories: string[];
  name: Record<string, string>;
  active: boolean;
  sortOrder: number;
  thumbnailUrl: string | null;
  /** Recommended paper ids (never a hard limit; empty = no recommendation). */
  paperIds: string[];
  /** Paper preselected on apply / used as the thumbnail backdrop. */
  showcasePaperId: string | null;
}

export function useTemplateLibrary() {
  return useQuery({
    queryKey: templateKeys.library(),
    queryFn: async () => {
      const res = await axiosInstance.get<TemplateLibraryItem[]>('/template/library');
      return res.data;
    },
    staleTime: 30_000,
  });
}
